import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Card,
  FormControl,
  IconButton,
  TablePagination,
} from '@mui/material';
import SearchBar from '../../../common/search-bar/search-bar';
import { BranchType } from '../../../../interfaces/delivery-company/branches';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { IWarehouseShelf } from '../../../../interfaces/delivery-company/warehouseshelf';
import { GenericList } from '../../../common/generic-list/generic-list';
import DownloadIcon from '@mui/icons-material/Download';
import EditShelfDialog from './edit-shelf-dialog';
import ShelfQRCodeDownload from './shelf-qrcode-dialog';
import SortIcon from '@mui/icons-material/Sort';
import LabelIcon from '@mui/icons-material/Label';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../../common/styles/components';
import ItemsCount from '../../../common/labels-input/items-count/items-count';
import { CompactDriverType } from '../../../../interfaces/delivery-company/drivers';
import AutocompleteWithThrottle from '../../../common/autocomplete-with-throttle/autocomplete-with-throttle';
import driver from '../../driver/driver';

function ShelvesListing() {
  const [shelvesList, setShelvesList] = useState<IWarehouseShelf[]>([]);
  const [shelvesCount, setShelvesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [editingShelf, setEditingShelf] = useState<IWarehouseShelf | null>(
    null
  );
  const [downloadShelfQRCode, setDownloadShelfQRCode] =
    useState<IWarehouseShelf | null>(null);
  const [shelf, setShelf] = useState<IWarehouseShelf | null>(null);

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const relevant_fields = ['id', 'label', 'package_count'];

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/shelves/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      try {
        const response = await axios.get<{
          count: number;
          results: IWarehouseShelf[];
        }>(endpoint, config);
        console.log(response);
        setResponse(response);
        setShelvesList(response.data.results);
        setShelvesCount(response.data.count);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setResponse(error.response);
          setMessage('Failed to load shelves: ' + error.response?.statusText);
        } else {
          setMessage('Failed to load shelves');
        }
      }
      setIsLoading(false);
    };
    getData();
  }, [controller]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const handleFetchShelves = async (
    query: string
  ): Promise<IWarehouseShelf[]> => {
    const endpoint = `${API_ENDPOINT}/api/v1/shelves/?page_size=10&label=${encodeURIComponent(query)}`;
    try {
      const response = await axios.get<{
        count: number;
        results: IWarehouseShelf[];
      }>(endpoint, config);
      return response.data.results;
    } catch (e) {
      console.log('failed to fetch shelves');
    }
    return [];
  };

  let displayedShelves = shelvesList;
  if (shelf) displayedShelves = [shelf];

  return (
    <Box>
      <FormControl fullWidth>
        <AutocompleteWithThrottle
          label={'Search shelf label'}
          required={false}
          fetch={handleFetchShelves}
          value={shelf}
          getOptionLabel={(option: IWarehouseShelf) => option.label}
          onChange={(e, value) => {
            setShelf(value);
          }}
        />
      </FormControl>
      <GenericList<IWarehouseShelf>
        sourceView="shelves-listing"
        items={displayedShelves}
        selectedItems={new Map()}
        isLoading={isLoading}
        fullWidth
        columns={[
          {
            id: 'id',
            header: <b>Shelf Number</b>,
            display_body: (s: IWarehouseShelf) => (
              <div
                style={{
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                onClick={() => setEditingShelf(s)}
              >
                {s.id}
              </div>
            ),
          },
          {
            id: 'label',
            header: <b>Shelf Label</b>,
            display_body: (s: IWarehouseShelf) => <b>{s.label}</b>,
          },
          {
            id: 'Packages',
            header: <b>Packages</b>,
            display_body: (s: IWarehouseShelf) => <>{s.package_count ?? ''}</>,
          },
          {
            id: 'qrcode',
            header: <b>QR Code</b>,
            display_body: (s: IWarehouseShelf) => (
              <IconButton onClick={() => setDownloadShelfQRCode(s)}>
                <DownloadIcon fontSize="large" color="primary" />
              </IconButton>
            ),
          },
        ]}
        getItemUID={(s) => s.id}
        rowConfigs={[
          {
            display_row: (item) =>
              mobileDoubleItemsGrid([
                <div key={'shelf id'}>Shelf #{<b>{item.id}</b>}</div>,
                <IconWithText centered key={'label'}>
                  <LabelIcon color="primary" /> {item.label}
                </IconWithText>,
              ]),
          },
          {
            display_row: (item) =>
              mobileDoubleItemsGrid([
                <div
                  style={{ width: '100%', textAlign: 'center' }}
                  key={'package count'}
                >
                  <ItemsCount label={`# items: ${item.package_count}`} />
                </div>,
                <div
                  key={'qrcode'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    alignContent: 'center',
                    justifyContent: 'center',
                    justifyItems: 'center',
                  }}
                >
                  <b>Shelf QR</b>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setDownloadShelfQRCode(item);
                    }}
                  >
                    <DownloadIcon fontSize="large" color="primary" />
                  </IconButton>
                </div>,
              ]),
          },
        ]}
        onCardClick={(item) => setEditingShelf(item)}
        cardIcon={(item) => <SortIcon color="primary" />}
      />

      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={shelf ? 1 : shelvesCount}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />

      {editingShelf && (
        <EditShelfDialog
          shelf={editingShelf}
          onClose={() => setEditingShelf(null)}
        />
      )}
      {downloadShelfQRCode && (
        <ShelfQRCodeDownload
          shelf={downloadShelfQRCode}
          onClose={() => setDownloadShelfQRCode(null)}
        />
      )}
    </Box>
  );
}

export default ShelvesListing;
